/**
 * /app/pos/reporte — Arqueo y cierre de sesión de caja.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, TrendingUp, TrendingDown, RefreshCw, Minus, Loader2, Wifi, Download } from "lucide-react";
import { CrudEmptyState, CrudShellHeader, DataTable, Loading, Panel } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { apiGetJson, apiSendJson, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { useQz } from "@/lib/printing/use-qz";
import { getPrinterForRole, paperWidthChars } from "@/lib/printing/printer-service";
import { buildCashCloseEscPos } from "@/lib/printing/escpos-builder";
import { toast } from "sonner";

export const Route = createFileRoute("/app/pos/reporte")({
  component: PosReportePage,
  head: () => ({ meta: [{ title: "Arqueo de caja — POS" }] }),
});

interface SessionReport {
  session: {
    id: string;
    status: string;
    initialAmount: number;
    openedAt: string;
    closingAmount?: number | null;
    expectedAmount?: number | null;
    difference?: number | null;
  };
  totalSales: number;
  totalReturns: number;
  totalIncomes: number;
  totalExpenses: number;
  cashExpected: number;
}

interface PastSession {
  id: string;
  status: string;
  openedAt: string;
  closedAt: string | null;
  initialAmount: number;
  closingAmount: number | null;
  expectedAmount: number | null;
  difference: number | null;
}

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

function PosReportePage() {
  const tid = useWorkspaceTenantId();
  const { tenantName } = useTenantContext();
  const { isReady, connect, print: qzPrint } = useQz();
  const [report, setReport] = useState<SessionReport | null>(null);
  const [pastSessions, setPastSessions] = useState<PastSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [closingAmount, setClosingAmount] = useState("");
  /** SCRUM-366: cuánto efectivo retira el cajero — vacío por defecto. */
  const [cashWithdrawal, setCashWithdrawal] = useState("");
  const [printing, setPrinting] = useState(false);

  const printerCfg = getPrinterForRole("cashier_printer");
  const hasPrinter = !!printerCfg?.printerName;

  const loadReport = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [sessionR, listR] = await Promise.all([
        apiGetJson(`/api/pos/session`, tid) as Promise<{ session?: { id: string } }>,
        apiGetJson(`/api/pos/session?action=list`, tid) as Promise<{ sessions?: PastSession[] }>,
      ]);
      setPastSessions(listR.sessions ?? []);
      if (!sessionR.session) {
        setReport(null);
        return;
      }
      const r = (await apiGetJson(
        `/api/pos/session?action=report&sessionId=${sessionR.session.id}`,
        tid,
      )) as { report?: SessionReport };
      setReport(r.report ?? null);
    } catch {
      toast.error("Error al cargar reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [tid]);

  const printCashClose = async (r: SessionReport, closingAmount: number, difference: number) => {
    if (!printerCfg?.printerName) return;
    setPrinting(true);
    try {
      if (!isReady) await connect();
      if (!window.qz?.websocket.isActive()) {
        throw new Error("QZ Tray no está conectado. Abrí QZ Tray en este dispositivo para imprimir el ticket de cierre.");
      }
      const width = paperWidthChars(printerCfg.paperWidth ?? "80mm");
      const escpos = buildCashCloseEscPos(
        {
          tenantName,
          sessionId: r.session.id,
          openedAt: r.session.openedAt,
          initialAmount: r.session.initialAmount,
          totalSales: r.totalSales,
          totalReturns: r.totalReturns,
          totalIncomes: r.totalIncomes,
          totalExpenses: r.totalExpenses,
          cashExpected: r.cashExpected,
          closingAmount,
          difference,
        },
        width,
      );
      await qzPrint(printerCfg.printerName, [{ type: "raw", format: "plain", data: escpos }], printerCfg.copies ?? 1);
      toast.success("Ticket de cierre impreso");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al imprimir el ticket de cierre");
    } finally {
      setPrinting(false);
    }
  };

  const handleClose = async () => {
    if (!report || !tid) return;
    const ca = parseFloat(closingAmount);
    if (isNaN(ca) || ca < 0) {
      toast.error("Ingresá el monto de cierre");
      return;
    }
    // SCRUM-366: retiro opcional, pero nunca puede superar el dinero contado.
    const withdrawal = cashWithdrawal.trim() ? parseFloat(cashWithdrawal) : 0;
    if (isNaN(withdrawal) || withdrawal < 0) {
      toast.error("El retiro de efectivo no es válido");
      return;
    }
    if (withdrawal > ca) {
      toast.error("El retiro no puede ser mayor al dinero contado");
      return;
    }
    setClosing(true);
    try {
      const diff = ca - report.cashExpected;
      await apiSendJson(
        `/api/pos/session`,
        "POST",
        {
          action: "close",
          sessionId: report.session.id,
          closingAmount: ca,
          expectedAmount: report.cashExpected,
          cashWithdrawal: withdrawal,
        },
        tid,
      );
      toast.success(
        diff === 0
          ? "Sesión cerrada sin diferencia"
          : `Sesión cerrada · diferencia ${pesos(diff)} (queda registrada)`,
      );
      const closedReport = report;
      setClosingAmount("");
      setCashWithdrawal("");
      loadReport();
      if (hasPrinter) void printCashClose(closedReport, ca, diff);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al cerrar sesión");
    } finally {
      setClosing(false);
    }
  };

  // SCRUM-367: descarga el PDF de cierre de una sesión desde el historial.
  const downloadReportPdf = async (sessionId: string) => {
    if (!tid) return;
    try {
      const res = await fetch(`/api/pos/session/report-pdf?sessionId=${sessionId}`, {
        credentials: "include",
        headers: clientRequestHeaders(tid),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cierre-caja-${sessionId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF");
    }
  };

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Reporte de caja" description="Arqueo, cierre y historial de diferencias" />
        <Loading variant="list" rows={3} />
      </div>
    );
  }

  const closedHistory = pastSessions.filter((s) => s.status === "closed").slice(0, 15);

  const historyColumns: DataTableColumn<PastSession>[] = [
    {
      key: "apertura",
      header: "Apertura",
      render: (s) => (
        <span className="text-xs">
          {new Date(s.openedAt).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })}
        </span>
      ),
    },
    {
      key: "cierre",
      header: "Cierre",
      render: (s) => (
        <span className="text-xs">
          {s.closedAt
            ? new Date(s.closedAt).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
            : "—"}
        </span>
      ),
    },
    {
      key: "esperado",
      header: "Esperado",
      className: "text-right",
      render: (s) => <span className="tabular-nums">{s.expectedAmount != null ? pesos(s.expectedAmount) : "—"}</span>,
    },
    {
      key: "contado",
      header: "Contado",
      className: "text-right",
      render: (s) => <span className="tabular-nums">{s.closingAmount != null ? pesos(s.closingAmount) : "—"}</span>,
    },
    {
      key: "diferencia",
      header: "Diferencia",
      className: "text-right",
      render: (s) => (
        <span
          className={`tabular-nums font-medium ${(s.difference ?? 0) < 0 ? "text-red-400" : (s.difference ?? 0) > 0 ? "text-green-400" : ""}`}
        >
          {s.difference != null ? pesos(s.difference) : "—"}
        </span>
      ),
    },
    {
      key: "pdf",
      header: "",
      className: "text-right",
      render: (s) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => void downloadReportPdf(s.id)}
          data-testid="pos-session-download-pdf"
        >
          <Download className="h-3.5 w-3.5" /> PDF
        </Button>
      ),
    },
  ];

  return (
    <div>
      <CrudShellHeader
        title="Reporte de caja"
        description="Arqueo, cierre y historial de diferencias"
      />
      <ClinicalPageShell offset="none">
        {!report ? (
        <CrudEmptyState
          icon={<FileText className="h-8 w-8" />}
          title="No hay sesión activa para reportar"
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/app/pos">Ir a POS</Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            El <strong className="text-foreground">efectivo esperado</strong> es: caja inicial + ventas en
            efectivo (menos vuelto) + ingresos − egresos. Tarjeta/QR/transferencia no suman al cajón.
            La <strong className="text-foreground">diferencia</strong> = contado − esperado (negativa =
            falta plata).
          </p>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { label: "Ventas (todas)", value: report.totalSales, icon: TrendingUp, color: "text-green-400" },
              {
                label: "Devoluciones",
                value: -report.totalReturns,
                icon: RefreshCw,
                color: "text-amber-400",
              },
              {
                label: "Ingresos extras",
                value: report.totalIncomes,
                icon: TrendingUp,
                color: "text-blue-400",
              },
              { label: "Egresos", value: -report.totalExpenses, icon: TrendingDown, color: "text-red-400" },
              {
                label: "Efectivo inicial",
                value: report.session.initialAmount,
                icon: Minus,
                color: "text-muted-foreground",
              },
              {
                label: "Efectivo esperado",
                value: report.cashExpected,
                icon: FileText,
                color: "text-primary",
              },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="rounded-xl border border-border/60 bg-card/80 p-4">
                <div className={`mb-1 flex items-center gap-1.5 text-xs ${color}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </div>
                <p className={`text-lg font-semibold tabular-nums ${color}`}>{pesos(value)}</p>
              </div>
            ))}
          </div>

          {report.session.status === "open" && (
            <Panel title="Cerrar sesión">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Efectivo contado en cajón</label>
                  <input
                    type="number"
                    min="0"
                    value={closingAmount}
                    onChange={(e) => setClosingAmount(e.target.value)}
                    placeholder={String(report.cashExpected)}
                    className="w-40 rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm text-right tabular-nums"
                  />
                </div>
                {closingAmount && (
                  <div className="flex flex-col gap-1">
                    <p className="text-xs text-muted-foreground">Diferencia</p>
                    <p
                      className={`text-sm font-semibold tabular-nums ${parseFloat(closingAmount) - report.cashExpected < 0 ? "text-red-400" : "text-green-400"}`}
                    >
                      {pesos(parseFloat(closingAmount) - report.cashExpected)}
                    </p>
                  </div>
                )}
              </div>
              {/* SCRUM-366: retiro de efectivo + lo que queda en caja para la próxima apertura */}
              <div className="mt-3 flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Retiro de efectivo</label>
                  <input
                    type="number"
                    min="0"
                    max={closingAmount || undefined}
                    value={cashWithdrawal}
                    onChange={(e) => setCashWithdrawal(e.target.value)}
                    placeholder="0"
                    data-testid="pos-cash-withdrawal"
                    className="w-40 rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm text-right tabular-nums"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">Efectivo en caja</p>
                  <p className="text-sm font-semibold tabular-nums" data-testid="pos-cash-left-for-next">
                    {pesos(
                      Math.max(
                        0,
                        (parseFloat(closingAmount) || 0) - (parseFloat(cashWithdrawal) || 0),
                      ),
                    )}
                  </p>
                </div>
              </div>
              <Button className="mt-3" size="sm" onClick={handleClose} disabled={closing}>
                {closing ? "Cerrando..." : "Confirmar cierre"}
              </Button>
              {!hasPrinter && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Sin impresora configurada — no se va a imprimir el ticket de cierre.{" "}
                  <a href="/app/settings/printers" className="underline">Configurar QZ Tray</a>
                </p>
              )}
            </Panel>
          )}

          {report.session.status === "closed" && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-muted bg-muted/20 text-muted-foreground">
                Sesión cerrada
                {report.session.difference != null && ` · diff ${pesos(report.session.difference)}`}
              </Badge>
              {hasPrinter && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={printing}
                  onClick={() =>
                    void printCashClose(
                      report,
                      report.session.closingAmount ?? 0,
                      report.session.difference ?? 0,
                    )
                  }
                  className="gap-1.5"
                >
                  {printing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
                  {printing ? "Imprimiendo…" : "Reimprimir ticket"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {closedHistory.length > 0 && (
        <div className="mt-8 space-y-3">
          <h3 className="text-sm font-medium">Historial de cierres</h3>
          <DataTable columns={historyColumns} rows={closedHistory} getRowKey={(s) => s.id} />
        </div>
      )}
    </ClinicalPageShell>
    </div>
  );
}
